// Wall geometry the take-off and the trim share. Moved verbatim out of
// roomTakeOffs.js.

const { getWallAxisXZ } = require('../geometry/wallAxis');

// A wall's extent along its own axis: the [minU, maxU] its corners project to
// (u measured along `getWallAxisXZ`'s direction), or null with no usable corners.
const wallProjectedSpan = (wall) => {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 2) return null;
  const { dirX, dirZ } = getWallAxisXZ(wall);
  let minU = Infinity;
  let maxU = -Infinity;
  for (const c of corners) {
    const cx = c?.x ?? (Array.isArray(c) ? c[0] : 0);
    const cz = c?.z ?? (Array.isArray(c) ? c[2] : 0);
    const u = cx * dirX + cz * dirZ;
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
  }
  return isFinite(minU) && isFinite(maxU) && maxU > minU
    ? { minU, maxU }
    : null;
};

const wallLengthM = (wall) => {
  const span = wallProjectedSpan(wall);
  if (span) return span.maxU - span.minU;
  const dx = wall?.dimensions?.x;
  return typeof dx === 'number' ? dx : 0;
};

const isOpeningWithinWallSpan = (opening, wall) => {
  if (!opening?.center || !wall) return false;
  const { dirX, dirZ, len, midX, midZ } = getWallAxisXZ(wall);
  if (!len) return false;
  const dx = opening.center.x - midX;
  const dz = opening.center.z - midZ;
  const perpDist = Math.abs(-dirZ * dx + dirX * dz);
  if (perpDist > 0.3) return false;
  const along = dirX * dx + dirZ * dz;
  return Math.abs(along) <= len / 2 + 0.05;
};

const angleToWall = (obj, wall) =>
  (obj?.rotation?.y || 0) - (wall?.rotation?.y || 0);
const objAlongExtent = (obj, wall) => {
  const d = angleToWall(obj, wall);
  const ox = obj?.dimensions?.x || 0;
  const oz = obj?.dimensions?.z || 0;
  return Math.abs(ox * Math.cos(d)) + Math.abs(oz * Math.sin(d));
};
const objPerpExtent = (obj, wall) => {
  const d = angleToWall(obj, wall);
  const ox = obj?.dimensions?.x || 0;
  const oz = obj?.dimensions?.z || 0;
  return Math.abs(ox * Math.sin(d)) + Math.abs(oz * Math.cos(d));
};
const objPerpDistToWall = (obj, wall) => {
  if (!obj?.center || !wall) return Infinity;
  const { dirX, dirZ, midX, midZ } = getWallAxisXZ(wall);
  const dx = obj.center.x - midX;
  const dz = obj.center.z - midZ;
  return Math.abs(-dirZ * dx + dirX * dz);
};
const objAlongWall = (obj, wall) => {
  if (!obj?.center || !wall) return 0;
  const { dirX, dirZ, midX, midZ } = getWallAxisXZ(wall);
  const dx = obj.center.x - midX;
  const dz = obj.center.z - midZ;
  return dirX * dx + dirZ * dz;
};
const objAgainstWall = (obj, wall) => {
  const wallLen = wallLengthM(wall);
  const along = Math.abs(objAlongWall(obj, wall));
  const perpDist = objPerpDistToWall(obj, wall);
  const perpExt = objPerpExtent(obj, wall);
  const alongExt = objAlongExtent(obj, wall);
  if (perpDist > perpExt / 2 + 0.1) return false;
  if (along > wallLen / 2 + alongExt / 2) return false;
  return true;
};

/** Today's `getOpeningsOnWall`, with the opening list passed in. */
const openingsOnWall = (wall, openings) =>
  (openings || []).filter((o) => {
    const parentId = o?.parentWallIds?.[0];
    if (parentId && wall.id) {
      if (parentId === wall.id) return true;
      if (parentId === wall.alignment?.structureWallId) {
        return isOpeningWithinWallSpan(o, wall);
      }
      return false;
    }
    return isOpeningWithinWallSpan(o, wall);
  });

/**
 * Where the take-off's along-wall coordinate starts on the plan: `a = 0` of
 * the wall-start frame is `a = -len/2` of the centreline frame the take-off
 * measures in (`objAlongWall`), so a span `[s, e]` from the wall start is the
 * plan segment `origin + dir·s → origin + dir·e`. Null for a wall with no
 * length.
 */
const wallStartAxis = (wall) => {
  const lenM = wallLengthM(wall);
  if (!(lenM > 0)) return null;
  const { dirX, dirZ, midX, midZ } = getWallAxisXZ(wall);
  return {
    origin: { x: midX - (dirX * lenM) / 2, z: midZ - (dirZ * lenM) / 2 },
    dir: { x: dirX, z: dirZ },
    lenM,
  };
};

module.exports = {
  wallProjectedSpan,
  wallLengthM,
  isOpeningWithinWallSpan,
  objAlongExtent,
  objPerpExtent,
  objPerpDistToWall,
  objAlongWall,
  objAgainstWall,
  openingsOnWall,
  wallStartAxis,
};
